// durable: the browser-facing client for the analytics adapter (ADR-0012)
//
// Every call goes to `/api/analytics/*`, which either host serves: the Worker
// in production, the Vite dev plugin locally. The client reports which source
// answered (`live` service or committed `fixture` envelope) and never
// recomputes a number the envelope already carries — the envelope is the
// contract's answer, not a raw material.

import type {
  AgentTrace,
  AnalysisReplay,
  AnalysisReplayRequest,
  AnalysisRequest,
  AnalysisResult,
} from './v1';

/** An envelope plus the provenance of the answer. */
export type AnalysisRun = {
  result: AnalysisResult;
  source: 'live' | 'fixture';
  /** Present only when a fixture answered in place of an unreachable service. */
  fallbackReason?: string;
};

/** A replay frame list plus the provenance of the answer. */
export type AnalysisReplayRun = {
  frames: AnalysisReplay;
  source: 'live' | 'fixture';
  fallbackReason?: string;
};

/**
 * One committed envelope, as projected by `GET /api/analytics/fixtures`. The
 * field types are projected from `AnalysisResult` rather than restated, so a
 * contract change cannot leave this behind.
 */
export type FixtureListing = {
  name: string;
  window: AnalysisResult['window'];
  material_hash: AnalysisResult['material_hash'];
  status: AnalysisResult['status'];
};

/** The service could not be reached and no committed envelope fitted. */
export class AnalyticsUnavailableError extends Error {
  readonly reason: string;

  constructor(reason: string) {
    super(`analytics service unavailable: ${reason}`);
    this.name = 'AnalyticsUnavailableError';
    this.reason = reason;
  }
}

/** The service answered, and the request did not satisfy the contract. */
export class AnalyticsContractError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'AnalyticsContractError';
    this.code = code;
  }
}

const RUNS_PATH = '/api/analytics/runs';
const REPLAYS_PATH = '/api/analytics/replays';
const FIXTURES_PATH = '/api/analytics/fixtures';

const SOURCE_HEADER = 'x-analytics-source';
const FALLBACK_REASON_HEADER = 'x-analytics-fallback-reason';

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

/** Evaluate one request; the service when it answers, the named fixture when it cannot. */
export async function runAnalysis(
  request: AnalysisRequest,
  opts: { fixture?: string; signal?: AbortSignal } = {},
): Promise<AnalysisRun> {
  const query = opts.fixture ? `?fixture=${encodeURIComponent(opts.fixture)}` : '';
  const response = await fetch(`${RUNS_PATH}${query}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(request),
    signal: opts.signal,
  });

  const text = await response.text();

  if (response.status === 503) {
    throw new AnalyticsUnavailableError(unavailableReason(text));
  }
  if (response.status === 422) {
    throw contractError(text);
  }
  if (!response.ok) {
    // The adapter only emits 400 for a malformed body and 404 for an unknown
    // route; a real 4xx/5xx here is a bug on the way, not a contract verdict.
    throw new Error(`analytics run failed: ${response.status} ${text.slice(0, 200)}`);
  }

  const source = response.headers.get(SOURCE_HEADER) === 'live' ? 'live' : 'fixture';
  const fallbackReason = response.headers.get(FALLBACK_REASON_HEADER) ?? undefined;
  return { result: JSON.parse(text) as AnalysisResult, source, fallbackReason };
}

/** Replay one request point in time; the service when it answers, its recording when it cannot. */
export async function replayAnalysis(
  request: AnalysisRequest,
  opts: { fixture?: string; step?: string; signal?: AbortSignal } = {},
): Promise<AnalysisReplayRun> {
  const step = opts.step ?? 'PT20M';
  const query = new URLSearchParams({ step });
  if (opts.fixture) query.set('fixture', opts.fixture);
  const body: AnalysisReplayRequest = { request, step };
  const response = await fetch(`${REPLAYS_PATH}?${query.toString()}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal: opts.signal,
  });

  const text = await response.text();
  if (response.status === 503) throw new AnalyticsUnavailableError(unavailableReason(text));
  if (response.status === 422) throw contractError(text);
  if (!response.ok) {
    throw new Error(`analytics replay failed: ${response.status} ${text.slice(0, 200)}`);
  }

  const source = response.headers.get(SOURCE_HEADER) === 'live' ? 'live' : 'fixture';
  const fallbackReason = response.headers.get(FALLBACK_REASON_HEADER) ?? undefined;
  return { frames: JSON.parse(text) as AnalysisReplay, source, fallbackReason };
}

/** The committed envelopes available to fall back to. */
export async function listFixtures(): Promise<FixtureListing[]> {
  const response = await fetch(FIXTURES_PATH);
  if (!response.ok) {
    throw new Error(`analytics fixtures failed: ${response.status}`);
  }
  const body = (await response.json()) as { fixtures?: FixtureListing[] };
  return body.fixtures ?? [];
}

/** The normalized input beside a committed fixture, sent unchanged to replayAnalysis. */
export async function loadFixtureRequest(name: string): Promise<AnalysisRequest> {
  const response = await fetch(`${FIXTURES_PATH}/${encodeURIComponent(name)}/request`);
  if (!response.ok) {
    throw new Error(`analytics fixture request "${name}" failed: ${response.status}`);
  }
  return (await response.json()) as AnalysisRequest;
}

/** One committed envelope by name, exactly as the service produced it. */
export async function loadFixtureResult(name: string): Promise<AnalysisResult> {
  const response = await fetch(`${FIXTURES_PATH}/${encodeURIComponent(name)}/result`);
  if (!response.ok) {
    throw new Error(`analytics fixture "${name}" failed: ${response.status}`);
  }
  return (await response.json()) as AnalysisResult;
}

/** The committed point-in-time frames produced by the Python service. */
export async function loadFixtureReplay(name: string, step = 'PT20M'): Promise<AnalysisReplay> {
  const query = new URLSearchParams({ step });
  const response = await fetch(`${FIXTURES_PATH}/${encodeURIComponent(name)}/replay?${query.toString()}`);
  if (!response.ok) {
    throw new Error(`analytics fixture replay "${name}" failed: ${response.status}`);
  }
  return (await response.json()) as AnalysisReplay;
}

/**
 * The resolved knowledge slices that fixture's envelope was computed against,
 * exactly as the fixture meta carries them. Callers label an envelope's ids
 * with these — the revision is the envelope's own `revisions`, never guessed.
 */
export async function loadFixtureSlices(name: string): Promise<AnalysisRequest['slices']> {
  const response = await fetch(`${FIXTURES_PATH}/${encodeURIComponent(name)}/slices`);
  if (!response.ok) {
    throw new Error(`analytics fixture "${name}" failed: ${response.status}`);
  }
  return (await response.json()) as AnalysisRequest['slices'];
}

/** The committed headless agent run recorded for a fixture; no live call is made. */
export async function loadFixtureAgentTrace(name: string): Promise<AgentTrace> {
  const response = await fetch(`${FIXTURES_PATH}/${encodeURIComponent(name)}/agent-trace`);
  if (!response.ok) {
    throw new Error(`analytics fixture agent trace "${name}" failed: ${response.status}`);
  }
  return (await response.json()) as AgentTrace;
}

// ---------------------------------------------------------------------------
// Error bodies — the service speaks two shapes
// ---------------------------------------------------------------------------

function unavailableReason(text: string): string {
  try {
    const body = JSON.parse(text) as { reason?: unknown };
    if (typeof body.reason === 'string') return body.reason;
  } catch {
    // Not JSON; fall through to the raw text below.
  }
  return text.slice(0, 200);
}

/**
 * A 422 arrives either as FastAPI's `{"detail":{"code","message"}}` or as a
 * full `failed` envelope carrying `errors[0]`; anything else is opaque and is
 * reported as such rather than guessed at.
 */
function contractError(text: string): AnalyticsContractError {
  let body: unknown;
  try {
    body = JSON.parse(text);
  } catch {
    return new AnalyticsContractError('unknown', text.slice(0, 200));
  }

  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === 'object' && detail !== null) {
    const { code, message } = detail as { code?: unknown; message?: unknown };
    if (typeof code === 'string' && typeof message === 'string') {
      return new AnalyticsContractError(code, message);
    }
  }

  const errors = (body as { errors?: unknown }).errors;
  if (Array.isArray(errors) && errors.length > 0) {
    const first = errors[0] as { code?: unknown; message?: unknown };
    if (typeof first?.code === 'string' && typeof first?.message === 'string') {
      return new AnalyticsContractError(first.code, first.message);
    }
  }

  return new AnalyticsContractError('unknown', text.slice(0, 200));
}
